#!/usr/bin/env node

import { Command } from 'commander';
import inquirer from 'inquirer';
import { MultiRepoMerge, ConfigManager } from './index';

const program = new Command();
const configManager = new ConfigManager();

program
  .name('supos-git')
  .description('Git tools')
  .version(process.env.PKG_VERSION || '0.0.1');

program
  .command('merge')
  .description('合并跨仓库分支')
  .option('-s, --source-repo <repo>', '源仓库名称')
  .option('-b, --source-branch <branch>', '源分支名称')
  .option('-t, --target-repo <repo>', '目标仓库名称')
  .option('-tb, --target-branch <branch>', '目标分支名称')
  .action(async (options) => {
    try {
      const repos = configManager.listRepos();
      if (repos.length === 0) {
        throw new Error('没有配置任何仓库，请先使用 config 命令配置仓库');
      }

      const answers = await inquirer.prompt([
        {
          type: 'list',
          name: 'sourceRepo',
          message: '请选择源仓库：',
          choices: repos.map((repo) => repo.name),
          when: !options.sourceRepo,
        },
        {
          type: 'input',
          name: 'sourceBranch',
          message: '请输入源分支名称：',
          when: !options.sourceBranch,
        },
        {
          type: 'list',
          name: 'targetRepo',
          message: '请选择目标仓库：',
          choices: repos.map((repo) => repo.name),
          when: !options.targetRepo,
        },
        {
          type: 'input',
          name: 'targetBranch',
          message: '请输入目标分支名称：',
          when: !options.targetBranch,
        },
      ]);

      const merger = new MultiRepoMerge();
      await merger.mergeCrossBranch(
        options.sourceRepo || answers.sourceRepo,
        options.sourceBranch || answers.sourceBranch,
        options.targetRepo || answers.targetRepo,
        options.targetBranch || answers.targetBranch
      );
      console.log('分支合并成功');
    } catch (error: any) {
      console.error('分支合并失败:', error.message);
      process.exit(1);
    }
  });

program
  .command('remote-merge')
  .description('合并远程分支')
  .option('-s, --source-repo <repo>', '源仓库名称')
  .option('-b, --source-branch <branch>', '源分支名称')
  .option('-t, --target-repo <repo>', '目标仓库名称')
  .option('-tb, --target-branch <branch>', '目标分支名称')
  .option('-d, --show-diff', '显示将要合并的差异')
  .option('-c, --conflict-strategy <strategy>', '合并冲突时的自动解决策略 (ours/theirs)')
  .option('-np, --no-push', '不推送合并结果')
  .action(async (options) => {
    try {
      const repos = configManager.listRepos();
      if (repos.length === 0) {
        throw new Error('没有配置任何仓库，请先使用 config 命令配置仓库');
      }

      // 检查是否有历史记录
      const history = configManager.getRemoteMergeHistory();
      let useHistory = false;

      if (history && !options.sourceRepo && !options.sourceBranch && !options.targetRepo && !options.targetBranch) {
        // 构建历史记录的显示信息，包括合并选项
        let historyMessage = `是否使用上次的合并配置？\n源仓库: ${history.sourceRepo}\n源分支: ${history.sourceBranch}\n目标仓库: ${history.targetRepo}\n目标分支: ${history.targetBranch}`;

        // 添加合并选项信息
        if (history.options) {
          historyMessage += '\n合并选项:';
          if (history.options.showDiff !== undefined) {
            historyMessage += `\n- 显示差异: ${history.options.showDiff ? '是' : '否'}`;
          }
          if (history.options.conflictStrategy) {
            historyMessage += `\n- 冲突解决策略: ${history.options.conflictStrategy === 'ours' ? '使用我们的更改 (ours)' : '使用他们的更改 (theirs)'}`;
          } else {
            historyMessage += '\n- 冲突解决策略: 不自动解决';
          }
          if (history.options.push !== undefined) {
            historyMessage += `\n- 推送合并结果: ${history.options.push ? '是' : '否'}`;
          }
        }

        const { confirm } = await inquirer.prompt([
          {
            type: 'confirm',
            name: 'confirm',
            message: historyMessage,
            default: true,
          },
        ]);
        useHistory = confirm;
      }

      const answers = await inquirer.prompt([
        {
          type: 'list',
          name: 'sourceRepo',
          message: '请选择源仓库：',
          choices: repos.map((repo) => repo.name),
          when: !useHistory && !options.sourceRepo,
        },
        {
          type: 'list',
          name: 'sourceBranch',
          message: '请选择源分支：',
          when: !useHistory && !options.sourceBranch,
          async choices(answers) {
            const sourceRepo = answers.sourceRepo;
            const merger = new MultiRepoMerge();
            const repo = configManager.getRepo(sourceRepo);
            if (!repo) {
              throw new Error(`找不到仓库配置: ${sourceRepo}`);
            }
            const git = await merger.initRepo(repo);
            // console.log(`[${sourceRepo}] 获取远程分支列表...`);
            const remoteBranches = await git.listRemote(['--heads']);
            // console.log(`[${sourceRepo}] 远程分支列表:`, remoteBranches);

            const validBranches = remoteBranches
              .split('\n')
              .filter((line) => line.trim())
              .map((line) => {
                const match = line.match(/refs\/heads\/(.+)$/);
                return match ? match[1] : null;
              })
              .filter((branch) => branch !== null)
              .sort();

            if (validBranches.length === 0) {
              throw new Error(`[${sourceRepo}] 未找到任何有效的远程分支`);
            }

            return validBranches;
          },
        },
        {
          type: 'list',
          name: 'targetRepo',
          message: '请选择目标仓库：',
          choices: repos.map((repo) => repo.name),
          when: !useHistory && !options.targetRepo,
        },
        {
          type: 'list',
          name: 'targetBranch',
          message: '请选择目标分支：',
          when: !useHistory && !options.targetBranch,
          async choices(answers) {
            const targetRepo = answers.targetRepo;
            const merger = new MultiRepoMerge();
            const repo = configManager.getRepo(targetRepo);
            if (!repo) {
              throw new Error(`找不到仓库配置: ${targetRepo}`);
            }
            const git = await merger.initRepo(repo);
            const remoteBranches = await git.listRemote(['--heads']);

            const validBranches = remoteBranches
              .split('\n')
              .filter((line) => line.trim())
              .map((line) => {
                const match = line.match(/refs\/heads\/(.+)$/);
                return match ? match[1] : null;
              })
              .filter((branch) => branch !== null)
              .sort();

            if (validBranches.length === 0) {
              throw new Error(`[${targetRepo}] 未找到任何有效的远程分支`);
            }

            return validBranches;
          },
        },
        {
          type: 'confirm',
          name: 'showDiff',
          message: '是否显示将要合并的差异？',
          default: false,
          when: () => !useHistory && options.showDiff === undefined,
        },
        {
          type: 'list',
          name: 'conflictStrategy',
          message: '请选择合并冲突时的自动解决策略：',
          choices: [
            { name: '不自动解决', value: null },
            { name: '使用我们的更改 (ours)', value: 'ours' },
            { name: '使用他们的更改 (theirs)', value: 'theirs' },
          ],
          when: () => !useHistory && !options.conflictStrategy && options.conflictStrategy !== null,
        },
      ]);

      const merger = new MultiRepoMerge();
      try {
        // 合并选项
        const mergeOptions =
          useHistory && history?.options
            ? { ...history.options } // 使用历史记录中的选项
            : {
                showDiff: options.showDiff !== undefined ? options.showDiff : answers.showDiff,
                conflictStrategy: options.conflictStrategy || answers.conflictStrategy,
                push: options.push,
              };

        await merger.mergeRemoteBranches(
          useHistory ? history?.sourceRepo : options.sourceRepo || answers.sourceRepo,
          useHistory ? history?.sourceBranch : options.sourceBranch || answers.sourceBranch,
          useHistory ? history?.targetRepo : options.targetRepo || answers.targetRepo,
          useHistory ? history?.targetBranch : options.targetBranch || answers.targetBranch,
          mergeOptions
        );

        // 保存本次合并历史
        configManager.saveRemoteMergeHistory({
          sourceRepo: useHistory ? history?.sourceRepo : options.sourceRepo || answers.sourceRepo,
          sourceBranch: useHistory ? history?.sourceBranch : options.sourceBranch || answers.sourceBranch,
          targetRepo: useHistory ? history?.targetRepo : options.targetRepo || answers.targetRepo,
          targetBranch: useHistory ? history?.targetBranch : options.targetBranch || answers.targetBranch,
          options: mergeOptions,
          timestamp: Date.now(),
        });
        // console.log('远程分支合并成功');
      } catch (error: any) {
        // 检查错误信息是否包含"Already up to date"
        if (error.message && error.message.includes('Already up to date')) {
          console.log('分支已经是最新的，无需合并');
        } else {
          console.error('远程分支合并失败:', error.message);
          process.exit(1);
        }
      }
    } catch (error: any) {
      console.error('远程分支合并失败:', error.message);
      process.exit(1);
    }
  });

program
  .command('abort')
  .description('中止合并操作')
  .option('-r, --repo <repo>', '仓库名称')
  .action(async (options) => {
    try {
      if (!options.repo) {
        const repos = configManager.listRepos();
        const answers = await inquirer.prompt([
          {
            type: 'list',
            name: 'repo',
            message: '请选择要中止合并的仓库：',
            choices: repos.map((repo) => repo.name),
          },
        ]);
        options.repo = answers.repo;
      }

      const merger = new MultiRepoMerge();
      await merger.abortMerge(options.repo);
      console.log('成功中止合并操作');
    } catch (error: any) {
      console.error('中止合并失败:', error.message);
      process.exit(1);
    }
  });

program
  .command('resolve')
  .description('解决合并冲突')
  .option('-r, --repo <repo>', '仓库名称')
  .option('-s, --strategy <strategy>', '解决策略 (ours/theirs)')
  .action(async (options) => {
    try {
      const answers = await inquirer.prompt([
        {
          type: 'list',
          name: 'repo',
          message: '请选择要解决冲突的仓库：',
          choices: configManager.listRepos().map((repo) => repo.name),
          when: !options.repo,
        },
        {
          type: 'list',
          name: 'strategy',
          message: '请选择解决冲突的策略：',
          choices: [
            { name: '使用我们的更改 (ours)', value: 'ours' },
            { name: '使用他们的更改 (theirs)', value: 'theirs' },
          ],
          when: !options.strategy,
        },
      ]);

      const merger = new MultiRepoMerge();
      await merger.resolveConflicts(
        options.repo || answers.repo,
        (options.strategy || answers.strategy) as 'ours' | 'theirs'
      );
      console.log('成功解决冲突');
    } catch (error: any) {
      console.error('解决冲突失败:', error.message);
      process.exit(1);
    }
  });

program
  .command('config')
  .description('配置仓库信息')
  .option('-n, --name <name>', '仓库名称')
  .option('-u, --url <url>', '仓库URL')
  .option('-t, --token <token>', 'Git令牌')
  .option('--username <username>', '用户名')
  .option('--password <password>', '密码')
  .action(async (options) => {
    try {
      // 在这里先创建 configManager 实例，确保在验证函数中可以使用
      const localConfigManager = new ConfigManager();
      const answers = await inquirer.prompt([
        {
          type: 'input',
          name: 'name',
          message: '请输入仓库名称：',
          when: !options.name,
          validate: (input) => {
            if (!input) return '仓库名称不能为空';
            if (!/^[\w-]+$/.test(input)) return '仓库名称只能包含字母、数字、下划线和横线';
            const repos = localConfigManager.listRepos() || [];
            if (repos.some((repo) => repo.name === input)) return '仓库名称已存在';
            return true;
          },
        },
        {
          type: 'input',
          name: 'url',
          message: '请输入仓库URL：',
          when: !options.url,
          validate: (input) => {
            if (!input) return 'Git仓库URL不能为空';
            try {
              new URL(input);
              if (!input.endsWith('.git')) return 'Git仓库URL必须以.git结尾';
              return true;
            } catch {
              return '请输入有效的Git仓库URL';
            }
          },
        },
        {
          type: 'list',
          name: 'type',
          message: '请选择仓库类型：',
          choices: ['gitee', 'gitea'],
        },
        {
          type: 'list',
          name: 'authType',
          message: '请选择认证方式：',
          choices: ['token', 'password'],
        },
        {
          type: 'password',
          name: 'token',
          message: '请输入Git令牌：',
          when: (answers) => !options.token && answers.authType === 'token',
          validate: (input) => (input ? true : 'Git令牌不能为空'),
        },
        {
          type: 'input',
          name: 'username',
          message: '请输入用户名：',
          when: (answers) => !options.username && answers.authType === 'password',
          validate: (input) => (input ? true : '用户名不能为空'),
        },
        {
          type: 'password',
          name: 'password',
          message: '请输入密码：',
          when: (answers) => !options.password && answers.authType === 'password',
          validate: (input) => (input ? true : '密码不能为空'),
        },
      ]);

      // 验证认证信息的完整性
      const authInfo = {
        token: options.token || answers.token,
        username: options.username || answers.username,
        password: options.password || answers.password,
      };

      if (!authInfo.token && (!authInfo.username || !authInfo.password)) {
        throw new Error('必须提供Git令牌或用户名密码');
      }

      const configManager = new ConfigManager();
      const merger = new MultiRepoMerge();

      // 验证认证信息
      try {
        await merger.initRepo({
          name: options.name || answers.name,
          url: options.url || answers.url,
          type: answers.type,
          ...authInfo,
        });
      } catch (error: any) {
        throw new Error(`Git认证验证失败: ${error.message}`);
      }

      configManager.addRepo({
        name: options.name || answers.name,
        url: options.url || answers.url,
        type: answers.type,
        ...authInfo,
      });
      console.log('仓库配置已保存');
    } catch (error: any) {
      console.error('保存配置失败:', error.message);
      process.exit(1);
    }
  });

program
  .command('list')
  .description('列出所有已配置的仓库信息')
  .action(() => {
    try {
      const repos = configManager.listRepos();
      if (repos.length === 0) {
        console.log('没有配置任何仓库，请先使用 config 命令配置仓库');
        return;
      }

      console.log('\n已配置的仓库信息：');
      console.table(
        repos.map((repo) => ({
          名称: repo.name,
          URL: repo.url,
          类型: repo.type,
          认证方式: repo.token ? 'token' : 'password',
          用户名: repo.username || '-',
        }))
      );
    } catch (error: any) {
      console.error('获取仓库信息失败:', error.message);
      process.exit(1);
    }
  });

program
  .command('remove')
  .description('删除仓库配置')
  .option('-n, --name <name>', '仓库名称')
  .action(async (options) => {
    try {
      const repos = configManager.listRepos();
      if (repos.length === 0) {
        throw new Error('没有配置任何仓库，请先使用 config 命令配置仓库');
      }

      let repoName = options.name;
      if (!repoName) {
        const answers = await inquirer.prompt([
          {
            type: 'list',
            name: 'name',
            message: '请选择要删除的仓库：',
            choices: repos.map((repo) => repo.name),
          },
        ]);
        repoName = answers.name;
      }

      const confirm = await inquirer.prompt([
        {
          type: 'confirm',
          name: 'sure',
          message: `确定要删除仓库 "${repoName}" 的配置吗？`,
          default: false,
        },
      ]);

      if (confirm.sure) {
        configManager.removeRepo(repoName);
        console.log(`仓库 "${repoName}" 的配置已删除`);
      } else {
        console.log('操作已取消');
      }
    } catch (error: any) {
      console.error('删除仓库配置失败:', error.message);
      process.exit(1);
    }
  });

program
  .command('help')
  .description('显示帮助信息')
  .action(() => {
    console.log(`
使用说明：

  git-tools <命令> [选项]

可用命令：
  merge  合并跨仓库分支
    选项：
      -s, --source-repo <repo>      源仓库名称
      -b, --source-branch <branch>  源分支名称
      -t, --target-repo <repo>      目标仓库名称
      -tb, --target-branch <branch> 目标分支名称

  remote-merge  合并远程分支
    选项：
      -s, --source-repo <repo>      源仓库名称
      -b, --source-branch <branch>  源分支名称
      -t, --target-repo <repo>      目标仓库名称
      -tb, --target-branch <branch> 目标分支名称
      -d, --show-diff               显示将要合并的差异
      -c, --conflict-strategy <strategy> 合并冲突时的自动解决策略 (ours/theirs)
      -np, --no-push                不推送合并结果

  abort  中止合并操作
    选项：
      -r, --repo <repo>  仓库名称

  resolve  解决合并冲突
    选项：
      -r, --repo <repo>          仓库名称
      -s, --strategy <strategy>  解决策略 (ours/theirs)

  config  配置仓库信息
    选项：
      -n, --name <name>        仓库名称
      -u, --url <url>          仓库URL
      -t, --token <token>      Git令牌
      --username <username>    用户名
      --password <password>    密码

  remove  删除仓库配置
    选项：
      -n, --name <name>  仓库名称

  list  列出所有已配置的仓库信息

示例：
  # 配置仓库
  git-tools config -n myrepo -u https://github.com/user/repo.git -t your-token

  # 合并分支
  git-tools merge -s source-repo -b feature -t target-repo -tb main

  # 合并远程分支（优化版本）
  git-tools remote-merge -s source-repo -b feature -t target-repo -tb main -d -c ours

  # 解决冲突
  git-tools resolve -r myrepo -s ours

  # 删除仓库配置
  git-tools remove -n myrepo
`);
  });

program.parse();
